import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { monitoringAPI } from '../services/api';

export const MONITORING_REFRESH_INTERVAL_MS = 30 * 1000;
const EMPTY_ROWS = [];
const EMPTY_METRICS = {};

const LIVE_QUERY_OPTIONS = {
  staleTime: 10 * 1000,
  refetchInterval: MONITORING_REFRESH_INTERVAL_MS,
  refetchIntervalInBackground: false,
  refetchOnMount: 'always',
  refetchOnReconnect: 'always',
  refetchOnWindowFocus: 'always',
};

const responseRows = (response) => response.data?.results || response.data || [];

export const useMonitoringData = () => {
  const tasksQuery = useQuery({
    queryKey: ['monitoring', 'tasks', 'list'],
    queryFn: async () => responseRows(
      await monitoringAPI.getTaskExecutions({ page_size: 100, ordering: '-created_at' })
    ),
    ...LIVE_QUERY_OPTIONS,
  });

  const healthQuery = useQuery({
    queryKey: ['monitoring', 'health', 'current'],
    queryFn: async () => {
      const response = await monitoringAPI.getCurrentHealth();
      return {
        rows: response.data?.results || [],
        metrics: response.data?.metrics || {},
      };
    },
    ...LIVE_QUERY_OPTIONS,
  });

  const alertsQuery = useQuery({
    queryKey: ['monitoring', 'alerts', 'list'],
    queryFn: async () => responseRows(
      await monitoringAPI.getSystemAlerts({ page_size: 100, ordering: '-created_at' })
    ),
    ...LIVE_QUERY_OPTIONS,
  });

  const {
    refetch: refetchTasks,
    dataUpdatedAt: tasksUpdatedAt,
  } = tasksQuery;
  const {
    refetch: refetchHealth,
    dataUpdatedAt: healthUpdatedAt,
  } = healthQuery;
  const {
    refetch: refetchAlerts,
    dataUpdatedAt: alertsUpdatedAt,
  } = alertsQuery;

  const refreshAll = useCallback(async () => {
    const results = await Promise.all([
      refetchTasks(),
      refetchHealth(),
      refetchAlerts(),
    ]);
    const sourceNames = ['tasks', 'health', 'alerts'];
    return {
      failed: results
        .map((result, index) => (result.isError ? sourceNames[index] : null))
        .filter(Boolean),
    };
  }, [refetchAlerts, refetchHealth, refetchTasks]);

  const refreshDataType = useCallback(async (dataType) => {
    const refetchByType = {
      tasks: refetchTasks,
      health: refetchHealth,
      alerts: refetchAlerts,
    };
    const refetch = refetchByType[dataType];
    if (!refetch) {
      return { isError: true, error: new Error(`Unknown monitoring data type: ${dataType}`) };
    }
    return refetch();
  }, [refetchAlerts, refetchHealth, refetchTasks]);

  const taskExecutions = tasksQuery.data || EMPTY_ROWS;
  const systemHealth = Array.isArray(healthQuery.data)
    ? healthQuery.data
    : healthQuery.data?.rows || EMPTY_ROWS;
  const operationalMetrics = Array.isArray(healthQuery.data)
    ? EMPTY_METRICS
    : healthQuery.data?.metrics || EMPTY_METRICS;
  const systemAlerts = alertsQuery.data || EMPTY_ROWS;

  const errors = useMemo(() => ({
    ...(tasksQuery.isError ? { tasks: tasksQuery.error } : {}),
    ...(healthQuery.isError ? { health: healthQuery.error } : {}),
    ...(alertsQuery.isError ? { alerts: alertsQuery.error } : {}),
  }), [
    alertsQuery.error,
    alertsQuery.isError,
    healthQuery.error,
    healthQuery.isError,
    tasksQuery.error,
    tasksQuery.isError,
  ]);

  const lastUpdated = useMemo(() => {
    const timestamps = [tasksUpdatedAt, healthUpdatedAt, alertsUpdatedAt]
      .filter((timestamp) => timestamp > 0);
    return timestamps.length > 0 ? new Date(Math.min(...timestamps)) : null;
  }, [alertsUpdatedAt, healthUpdatedAt, tasksUpdatedAt]);

  const metrics = useMemo(() => ({
    totalTasks: operationalMetrics.total_tasks ?? taskExecutions.length,
    successfulTasks: operationalMetrics.successful_tasks
      ?? taskExecutions.filter((task) => task.task_status === 'SUCCESS').length,
    failedTasks: operationalMetrics.failed_tasks
      ?? taskExecutions.filter((task) => task.task_status === 'FAILURE').length,
    runningTasks: operationalMetrics.running_tasks
      ?? taskExecutions.filter((task) => task.task_status === 'STARTED').length,
    recentFailures: operationalMetrics.recent_failures ?? taskExecutions.filter((task) => {
      if (task.task_status !== 'FAILURE' || !task.completed_at) return false;
      const failureTime = new Date(task.completed_at);
      const hoursSince = (Date.now() - failureTime.getTime()) / (1000 * 60 * 60);
      return hoursSince <= 24;
    }).length,
    servicesDown: systemHealth.filter((health) => health.status === 'DOWN').length,
    servicesCritical: systemHealth.filter((health) => health.status === 'CRITICAL').length,
    totalServices: systemHealth.length,
    activeAlerts: operationalMetrics.active_alerts
      ?? systemAlerts.filter((alert) => !alert.resolved).length,
    criticalAlerts: operationalMetrics.critical_alerts ?? systemAlerts.filter(
      (alert) => !alert.resolved && alert.severity?.toLowerCase() === 'critical'
    ).length,
    highAlerts: operationalMetrics.high_alerts ?? systemAlerts.filter(
      (alert) => !alert.resolved && alert.severity?.toLowerCase() === 'high'
    ).length,
  }), [operationalMetrics, systemAlerts, systemHealth, taskExecutions]);

  return {
    taskExecutions,
    systemHealth,
    systemAlerts,
    errors,
    lastUpdated,
    metrics,
    loading: [tasksQuery, healthQuery, alertsQuery].some((query) => query.isPending),
    isRefreshing: [tasksQuery, healthQuery, alertsQuery].some(
      (query) => query.isFetching && !query.isPending
    ),
    refreshAll,
    refreshDataType,
  };
};

export const useAuditLogs = (params, enabled = true) => {
  return useQuery({
    queryKey: ['monitoring', 'audit', 'list', params],
    queryFn: async () => {
      const response = await monitoringAPI.getAuditLogs(params);
      const rows = response.data?.results || response.data || [];
      return {
        rows,
        count: response.data?.count ?? rows.length,
      };
    },
    enabled,
    staleTime: 10 * 1000,
    refetchInterval: enabled ? MONITORING_REFRESH_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });
};
